import { useI18n } from '@ecom/i18n';
import { useEffect, type ReactNode } from 'react';

import { cx } from '../utils/cx';

import { CloseIcon } from './CloseIcon';

export type ToastTone = 'neutral' | 'good' | 'critical';

export interface ToastProps {
  readonly open: boolean;
  readonly message: string;
  readonly tone?: ToastTone;
  /** Milliseconds before auto-dismissal. Pass `null` to require a manual dismiss. */
  readonly duration?: number | null;
  readonly onDismiss: () => void;
}

/**
 * Transient confirmation message (§7.1 primitives).
 *
 * The prototypes' `#toast` already gets the semantics right —
 * `role="status" aria-live="polite"` — and §7.1 calls that out explicitly as
 * correct, so it is preserved rather than "improved". Visual treatment is the
 * prototype's too: inverted surface (`--ecom-ink` background on
 * `--ecom-ground` text), pill radius, drop shadow, centred above the bottom
 * edge.
 *
 * `polite` rather than `assertive` is deliberate: a toast confirms something
 * the user just did. Interrupting whatever a screen reader is mid-sentence on
 * to say "added to cart" is worse than waiting for a pause. A toast is
 * therefore never the right carrier for an error the user must act on — that
 * belongs in `Field`'s `role="alert"`, next to the control at fault.
 *
 * The live region is only mounted while `open`, so the message is announced
 * as an insertion. Keeping an empty region permanently mounted and swapping
 * its text is the other valid pattern, but it makes repeat messages ("added
 * to cart" twice in a row) silent, since the text never changes.
 */
export function Toast({
  open,
  message,
  tone = 'neutral',
  duration = 4000,
  onDismiss,
}: ToastProps): ReactNode {
  useEffect(() => {
    if (!open || duration === null) return;

    const timer = setTimeout(onDismiss, duration);
    return () => {
      clearTimeout(timer);
    };
  }, [open, duration, onDismiss, message]);

  const { t } = useI18n();

  if (!open) return null;

  return (
    <div className={cx('ecom-toast', `ecom-toast--${tone}`)} role="status" aria-live="polite">
      {tone === 'good' ? <CheckIcon /> : null}
      <span className="ecom-toast__message">{message}</span>
      <button
        type="button"
        className="ecom-toast__dismiss"
        aria-label={t('common.dismiss')}
        onClick={onDismiss}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

/** The prototype's checkmark, shown only on the success tone. */
function CheckIcon(): ReactNode {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
